import React from "react";
import { motion } from "framer-motion";
import { CalendarIcon, UserIcon } from "@heroicons/react/24/outline";
import type { UseFormReturn } from "react-hook-form";
import Card from "./ui/Card";
import {
  TELEPHONE_NUMBER,
  EMAIL_ADDRESS,
  COMPANY_NAME,
  CURRENCY_SYMBOL,
} from "../../constants/constant";
import {
  useGetHotelListQuery,
  useGetServiceListQuery,
} from "../../services/common.service";
import { SKI_LEVELS, SNOWBOARD_LEVELS } from "../../types/booking";
import type { Participant, BookingForm, LessonDate } from "../../types/booking";

interface ServiceItem {
  id: string | number;
  name?: string;
  description?: string;
  cost?: number | string;
  service_mode?: string;
  category?: string;
}

interface BookingSummaryProps {
  form: UseFormReturn<BookingForm>;
  lessonDates: LessonDate[];
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  form,
  lessonDates,
}) => {
  const { watch } = form;
  const formData = watch();

  const { data: servicesData } = useGetServiceListQuery(
    "pagination=false&online_booking=Y&status=Active",
  ) as { data: { items: ServiceItem[] } | undefined };
  // Use the same data sources as the form
  const { data: hotelData } = useGetHotelListQuery(
    "pagination=false&type=hotel&online_booking=Y&status=Active&sortBy=name",
  );
  const { data: agentData } = useGetHotelListQuery(
    "pagination=false&type=agent",
  );

  const getHotelName = (hotelId: string) => {
    if (!hotelId) return "Not selected";

    // Use the dynamic hotel data from API
    const hotel = hotelData?.items?.find(
      (h: { id: string | number; name: string }) =>
        String(h.id) === String(hotelId),
    );
    if (hotel) return hotel.name;
    return "";
  };

  const getAgentName = (agentId: string) => {
    if (!agentId) return "None";

    // Use the dynamic agent data from API
    const agent = agentData?.items?.find(
      (a: { id: string | number; name: string }) =>
        String(a.id) === String(agentId),
    );
    if (agent) return agent.name;
    return "";
  };

  const formatDate = (date?: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) {
      return "Date not selected";
    }
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const getLessonTypeName = (lessonTypeId: string) => {
    if (!lessonTypeId) return { lessonName: "Not selected", price: "" };

    const apiService = servicesData?.items?.find(
      (s: ServiceItem) => String(s.id) === String(lessonTypeId),
    );
    if (apiService) {
      const desc = apiService.description;
      const mode =
        apiService.service_mode?.toLowerCase() ||
        apiService.category?.toLowerCase();
      const suffix =
        mode === "groups"
          ? " per person"
          : mode === "private"
            ? " per instructor"
            : "";
      const price = apiService.cost
        ? `(${CURRENCY_SYMBOL}${apiService.cost}${suffix})`
        : "";
      const result = desc ?? apiService.name ?? "Unknown Lesson Type";
      return { lessonName: result, price };
    }

    return { lessonName: "Unknown Lesson Type", price: "" };
  };

  const getSkillLevelName = (level: string) => {
    if (!level) return "Not selected";

    // Use the dynamic skill levels from types
    const allSkillLevels = [...SKI_LEVELS, ...SNOWBOARD_LEVELS];
    const skillLevel = allSkillLevels.find((l) => l === level);

    if (skillLevel) {
      // Return just the level code (e.g., "SK1", "SB2")
      return skillLevel;
    }

    return level;
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center mb-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Review Your Booking
        </h2>
        <p className="text-gray-600">
          Please review all the details below before submitting your booking
          request.
        </p>
      </motion.div>

      {/* Contact Information */}
      <Card className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <UserIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">
              Contact Information
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Name</p>
              <p className="text-gray-900">{formData.bookingPerson?.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-900">{formData.bookingPerson?.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Hotel</p>
              <p className="text-gray-900">
                {getHotelName(formData.bookingPerson?.hotelId || "")}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Agent</p>
              <p className="text-gray-900">
                {getAgentName(formData.bookingPerson?.agentId || "")}
              </p>
            </div>
            {formData.bookingPerson?.referenceNo && (
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Reference Number
                </p>
                <p className="text-gray-900">
                  {formData.bookingPerson.referenceNo}
                </p>
              </div>
            )}
            {formData.bookingPerson?.comments && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Comments</p>
                <p className="text-gray-900">
                  {formData.bookingPerson.comments}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </Card>

      {/* Lesson Details */}
      <Card className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <CalendarIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">Lesson Details</h3>
          </div>
          <div className="space-y-6">
            {lessonDates.map((lessonDate: LessonDate, index: number) => (
              <div
                key={lessonDate.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">
                    Lesson {index + 1} - {formatDate(lessonDate.date)}
                  </h4>
                </div>
                <div className="space-y-3">
                  {lessonDate.participants.map(
                    (participant: Participant, participantIndex: number) => (
                      <div
                        key={participant.id}
                        className="bg-gray-50 p-3 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900">
                            Participant {participantIndex + 1}:{" "}
                            {participant.name}
                          </h5>
                          {participant.age && (
                            <span className="text-sm text-gray-500">
                              Age: {participant.age}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-500">
                              Activity:
                            </span>
                            <br />
                            <span className="text-gray-900">
                              {participant.activity}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">
                              Time:
                            </span>
                            <br />
                            <span className="text-gray-900">
                              {participant.timeSlot}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">
                              Skill Level:
                            </span>
                            <br />
                            <span className="text-gray-900">
                              {getSkillLevelName(participant.skillLevel)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">
                              Lesson Type:
                            </span>
                            <br />
                            <span className="text-gray-900">
                              {
                                getLessonTypeName(participant.lessonType)
                                  .lessonName
                              }
                              {getLessonTypeName(participant.lessonType)
                                .price && (
                                <>
                                  <br />
                                  <span className="text-gray-600">
                                    {
                                      getLessonTypeName(participant.lessonType)
                                        .price
                                    }
                                  </span>
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </Card>

      {/* Terms & Conditions */}
      <Card className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <label
            htmlFor="acceptedTerms"
            className="flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              id="acceptedTerms"
              {...form.register("acceptedTerms")}
              className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer accent-blue-600"
            />
            <span className="text-gray-700 text-sm leading-relaxed">
              I have read and agree to the{" "}
              <a
                href="https://www.hakubasnowsports.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800 font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </a>
            </span>
          </label>
          {form.formState.errors.acceptedTerms && (
            <p className="text-red-600 text-sm mt-2 ml-8">
              {form.formState.errors.acceptedTerms.message}
            </p>
          )}
        </motion.div>
      </Card>

      {/* Company Information */}
      <Card className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="text-center text-blue-800">
            <h4 className="font-semibold text-lg">{COMPANY_NAME}</h4>
            <p className="mt-2">
              TEL:{" "}
              <a href={`tel:${TELEPHONE_NUMBER}`} className="hover:underline">
                {TELEPHONE_NUMBER}
              </a>
              <br />
              EMAIL:{" "}
              <a
                href={`mailto:${EMAIL_ADDRESS}`}
                className="text-blue-600 hover:underline"
              >
                {EMAIL_ADDRESS}
              </a>
            </p>
          </div>
        </motion.div>
      </Card>
    </div>
  );
};

export default BookingSummary;
